// Localização: frontend/src/api/assinaturaApi.js
// Serviço de API para comunicação com endpoints de assinaturas

import api from './api';

/**
 * Cria checkout de assinatura
 * @param {number} planoId - ID do plano a ser assinado
 * @returns {Promise} Resposta com init_point (link de checkout)
 */
export const createCheckout = async (planoId) => {
    try {
        const response = await api.post('/assinaturas/checkout', { plano_id: planoId });
        return response.data;
    } catch (error) {
        console.error('[AssinaturaAPI] Erro ao criar checkout:', error);
        throw error;
    }
};

/**
 * Busca assinatura atual da organização do usuário
 * @returns {Promise} Dados da assinatura ou null se não houver
 */
export const getAssinatura = async () => {
    try {
        const response = await api.get('/assinaturas/me');
        return response.data;
    } catch (error) {
        // Se for 404 ou não houver assinatura, não é erro crítico
        if (error.response?.status === 404 || error.response?.status === 200) {
            return { success: true, data: null };
        }
        console.error('[AssinaturaAPI] Erro ao buscar assinatura:', error);
        throw error;
    }
};

/**
 * Cancela uma assinatura
 * @param {number} assinaturaId - ID da assinatura a ser cancelada
 * @returns {Promise} Confirmação de cancelamento
 */
export const cancelAssinatura = async (assinaturaId) => {
    try {
        const response = await api.post(`/assinaturas/${assinaturaId}/cancelar`);
        return response.data;
    } catch (error) {
        console.error('[AssinaturaAPI] Erro ao cancelar assinatura:', error);
        throw error;
    }
};

/**
 * Lista todos os planos disponíveis
 * @returns {Promise} Lista de planos
 */
export const listPlanos = async () => {
    try {
        const response = await api.get('/assinaturas/planos');
        return response.data;
    } catch (error) {
        console.error('[AssinaturaAPI] Erro ao listar planos:', error);
        throw error;
    }
};

/**
 * Sincroniza o status da assinatura
 * NOTA: Integração com Mercado Pago removida. Esta função será reimplementada com Stripe.
 * @param {number} assinaturaId - ID da assinatura no banco de dados
 * @returns {Promise} Resposta com status atualizado
 */
export const sincronizarAssinatura = async (assinaturaId) => {
    // Função desabilitada - integração com Mercado Pago removida
    console.log('[AssinaturaAPI] ⚠️ Sincronização desabilitada - integração com Mercado Pago removida');
    return { 
        success: false, 
        message: 'Sincronização temporariamente indisponível. Será reimplementada com Stripe.' 
    };
};

/**
 * Busca notificações de assinatura para a organização
 * @returns {Promise<object>} Notificações de assinatura
 */
export const getNotificacoesAssinatura = async () => {
    try {
        const response = await api.get('/assinaturas/notificacoes');
        return response.data;
    } catch (error) {
        console.error('[AssinaturaAPI] Erro ao buscar notificações:', error);
        // Retornar objeto vazio em caso de erro para não quebrar a UI
        return { success: true, notificacoes: [], total: 0, tem_urgente: false };
    }
};

/**
 * Inicia período de trial Premium (14 dias) para usuário sem organização
 * @returns {Promise} Resposta com dados do trial iniciado
 */
export const iniciarPremiumTrial = async () => {
    try {
        const response = await api.post('/assinaturas/premium-trial/iniciar');
        return response.data;
    } catch (error) {
        console.error('[AssinaturaAPI] Erro ao iniciar trial Premium:', error);
        throw error;
    }
};